import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { ErrorMessage, InfoMessage } from "@msys/ui";
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { PimProductTypeSystemKey } from "../../../clients/graphqlTypes.js";
import { ButtonCircleWrapper } from "../../commons/button/ButtonCircleWrapper.js";
import { SwitchCollectionViewButton } from "../../commons/button/SwitchCollectionViewButton.js";
import {
  CollectionView,
  useCollectionViewWithMobile,
} from "../../commons/hooks/useCollectionView.js";
import { PAGE_LIST_RESULTS_PER_PAGE } from "../../constants.js";
import { ProductPreferenceButton } from "../../features/products/buttons/ProductPreferenceButton.js";
import { ProductsListView } from "../../features/products/components/ProductsListView.js";
import { ProductTypeAssignmentSearch } from "../../features/products/components/ProductTypeAssignmentSearch.js";
import { useGetProductTo } from "../../features/products/helper.js";
import { ProductSearchItem__ProductSearchResultFragment } from "../../features/products/Product.generated.js";
import { ProductPreferenceFragment } from "../../features/products/ProductPreferences.generated.js";
import { articleNumberIdentifierKey } from "../../features/products/productPreferences.js";
import { useProductPreferences } from "../../features/products/useProductPreferences.js";
import { useDataGridStateStore } from "../../features/users/useDataGridStateStore.js";
import { useProductPreferencesPage_ProductTypesQuery } from "./ProductPreferences.generated.js";
import { usePreferredProducts } from "./usePreferredProducts.js";

interface Props {
  productTypeKey: string;
  productTypeSystemKey: PimProductTypeSystemKey | null;
  currentProductTypeSystemKey: PimProductTypeSystemKey | null;
}

const ALLOWED_VIEWS: CollectionView[] = ["gallery", "list", "table"];

/**
 * The selected product type's preferred products, in the same list views the product search
 * offers, with the catalogue assignment as the screen's action.
 */
export function ProductTypePreferences({
  productTypeKey,
  productTypeSystemKey,
  currentProductTypeSystemKey,
}: Props) {
  const { t } = useTranslate(["Product", "Global"]);
  const client = useApolloClient();
  const stateStore = useDataGridStateStore("ProductTypePreferences");
  const getProductTo = useGetProductTo();

  const [mode, setMode] = React.useState<"preferences" | "assign">(
    "preferences"
  );
  const [activeView, setActiveView] =
    useCollectionViewWithMobile<CollectionView>(
      "product-preferences",
      "gallery",
      "list"
    );
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: PAGE_LIST_RESULTS_PER_PAGE,
  });

  // A current-system route carries no system in the URL. Omitting it would let PIM fall back
  // to its own default, which need not be the system the organisation selected.
  const resolvedProductTypeSystemKey =
    productTypeSystemKey ?? currentProductTypeSystemKey;

  const typeQuery = useProductPreferencesPage_ProductTypesQuery({
    client,
    variables: {
      productTypeKeys: [productTypeKey],
      productTypeSystemKey: resolvedProductTypeSystemKey,
      limit: 1,
    },
    skip: productTypeKey === "",
  });
  const productType =
    getDataOrNull(typeQuery.data?.pimSearchProductTypes)?.productTypes.find(
      type => type.key === productTypeKey
    ) ?? null;

  const preferences = useProductPreferences(
    productTypeKey === ""
      ? null
      : {
          productTypeKey,
          productTypeSystemKey: resolvedProductTypeSystemKey,
        }
  );

  const { loading: productsLoading, products } = usePreferredProducts(
    preferences.preferences
  );

  // Inert: PIM looks preferences up by the current system's key, so a preference stored under
  // a superseded system ranks nothing. Its articles stay listed and removable — an entry a
  // user cannot see is an entry they cannot remove — but nothing more may be assigned to it.
  const isInert =
    productTypeSystemKey !== null &&
    currentProductTypeSystemKey !== null &&
    productTypeSystemKey !== currentProductTypeSystemKey;

  const label = productType?.label ?? productTypeKey;

  const preferencesByArticle = React.useMemo(() => {
    const byArticle = new Map<string, ProductPreferenceFragment[]>();
    for (const preference of preferences.preferences) {
      const key = articleNumberIdentifierKey(
        preference.articleNumberIdentifier
      );
      byArticle.set(key, [...(byArticle.get(key) ?? []), preference]);
    }
    return byArticle;
  }, [preferences.preferences]);

  const removeAllScopes = React.useCallback(
    async (product: ProductSearchItem__ProductSearchResultFragment) => {
      const stored =
        preferencesByArticle.get(articleNumberIdentifierKey(product)) ?? [];
      try {
        for (const preference of stored) {
          await preferences.deleteProductPreferences({
            variables: {
              input: {
                scope: preference.scope,
                // Removal addresses the preference as it is stored, which for a superseded
                // type system is the only way to reach it at all.
                productType: {
                  productTypeKey: preference.productType.productTypeKey,
                  productTypeSystemKey:
                    preference.productType.productTypeSystemKey,
                },
                articleNumberIdentifiers: [
                  {
                    supplierId: product.supplierId,
                    articleNumber: product.articleNumber,
                  },
                ],
              },
            },
          });
        }
      } catch (error) {
        // Reported rather than swallowed, and no success message: the article may still
        // carry the scopes whose removal did not run.
        if (error instanceof Error)
          enqueueSnackbar(error.message, { variant: "error" });
        return;
      }
      enqueueSnackbar(t("Preference removed.", { ns: "Product" }), {
        variant: "success",
      });
    },
    [preferencesByArticle, preferences, t]
  );

  const ActionButton = React.useCallback(
    ({
      product,
    }: {
      product: ProductSearchItem__ProductSearchResultFragment;
    }) => {
      // Under a superseded product type system there is no id to write against, so the row
      // offers removal only — of every scope the article is stored at, since the scope menu
      // has nothing left to add.
      // Until the lookup has answered, "no product type" is not a statement about PIM.
      if (productType === null && (typeQuery.loading || typeQuery.error)) {
        return null;
      }
      if (isInert || productType === null) {
        const removeLabel = t("Remove preference", { ns: "Product" });
        return (
          <ButtonCircleWrapper>
            <Tooltip title={removeLabel}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  aria-label={removeLabel}
                  disabled={preferences.writing}
                  onClick={event => {
                    event.stopPropagation();
                    removeAllScopes(product);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </ButtonCircleWrapper>
        );
      }

      return (
        <ButtonCircleWrapper>
          <ProductPreferenceButton
            productTypeId={productType.id}
            product={product}
            preferences={preferences}
          />
        </ButtonCircleWrapper>
      );
    },
    [
      isInert,
      productType,
      typeQuery.loading,
      typeQuery.error,
      preferences,
      removeAllScopes,
      t,
    ]
  );

  // Removing the last rows of a later page shortens the list under the current page index.
  // Clamping keeps the view on the last page that still has rows, rather than on an empty
  // one whose list view renders no pagination to navigate away from.
  const lastPage = Math.max(
    0,
    Math.ceil(products.length / paginationModel.pageSize) - 1
  );
  const page = Math.min(paginationModel.page, lastPage);
  const clampedPaginationModel =
    page === paginationModel.page
      ? paginationModel
      : { ...paginationModel, page };

  const pageProducts = products.slice(
    page * paginationModel.pageSize,
    (page + 1) * paginationModel.pageSize
  );

  return (
    <Stack direction="column" spacing={2} sx={{ minHeight: 0, flex: 1 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", justifyContent: "space-between" }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: "baseline" }}>
          <Typography variant="h2">{label}</Typography>
          {/* The key stays next to a resolved title: it is what a preference is stored under. */}
          <Typography variant="body2" sx={{ color: "grey.600" }}>
            {productTypeKey}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          {mode === "preferences" ? (
            <SwitchCollectionViewButton
              allowedViews={ALLOWED_VIEWS}
              activeView={activeView}
              setActiveView={setActiveView}
            />
          ) : null}
          {mode === "assign" ? (
            <Button
              startIcon={<ArrowBackIcon />}
              size="small"
              onClick={() => setMode("preferences")}
            >
              {t("Preferred products", { ns: "Product" })}
            </Button>
          ) : !isInert && productType !== null ? (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setMode("assign")}
            >
              {t("Assign products from the catalogue", { ns: "Product" })}
            </Button>
          ) : null}
        </Stack>
      </Stack>
      {preferences.error || typeQuery.error ? (
        <ErrorMessage
          message={(preferences.error ?? typeQuery.error)?.message ?? ""}
        />
      ) : mode === "assign" && productType !== null ? (
        <ProductTypeAssignmentSearch
          productTypeId={productType.id}
          productTypeLabel={label}
          preferences={preferences}
        />
      ) : (
        <>
          {isInert ? (
            <InfoMessage
              message={t(
                "These preferences are stored under the {productTypeSystem} product type system, which is no longer in use. They do not affect the order of search results. Remove them if you no longer need them.",
                { ns: "Product", productTypeSystem: productTypeSystemKey }
              )}
            />
          ) : null}
          <ProductsListView
            items={pageProducts}
            totalCount={products.length}
            activeView={activeView}
            loading={!preferences.preferredStateKnown || productsLoading}
            paginationModel={clampedPaginationModel}
            setPaginationModel={setPaginationModel}
            // No sort props: the order is the preference list's own.
            getProductTo={getProductTo}
            CardActionButton={ActionButton}
            TableActions={ActionButton}
            stateStore={stateStore}
            tableActionsWidth={50}
          />
        </>
      )}
    </Stack>
  );
}
